import { InputNumber, Select } from 'antd';
import { IBestDataset, IPlotVariable } from '../../../../../../types/analytics';
import { Label } from '../../../../../common/Label/Label';
import { monthOptions } from '../../../../ResultPanel/Plot/MonthOptions';
import styles from '../../Layout/AnalyticsLayout.module.scss';

interface ScatterPlotAdditionalInputProps {
  categoryDataset: IBestDataset[];
  categories: string[];
  datasetY: string | null;
  onDatasetYChange: (datasetId: string) => void;
  variablesY: IPlotVariable[];
  variableY: IPlotVariable | null;
  xAxisInterval: number | null;
  yAxisInterval: number | null;
  month: number;
  onXAxisIntervalChange: (value: number | null) => void;
  onYAxisIntervalChange: (value: number | null) => void;
  onMonthChange: (value: number) => void;
  onVariableChange: (variableIndex: number, axis: 'X' | 'Y') => void;
  dataPreformanceWarning: boolean;
  isLoading?: boolean;
  hasError?: boolean;
}

export const ScatterPlotAdditionalInput = ({
  categoryDataset,
  categories,
  datasetY,
  onDatasetYChange,
  variablesY,
  variableY,
  onVariableChange,
  dataPreformanceWarning,
  isLoading = false,
  hasError = false,
  month,
  onMonthChange,
  xAxisInterval,
  yAxisInterval,
  onXAxisIntervalChange,
  onYAxisIntervalChange,
}: ScatterPlotAdditionalInputProps) => {
  return (
    <div className={styles.additionalControlsContainer}>
      <div className={styles.controlItem}>
        <Label label='Y-axis Dataset'>
          <Select
            id='Analytics-ScatterPlot-DatasetY_Input'
            value={datasetY}
            style={{ width: '100%' }}
            onChange={onDatasetYChange}
            disabled={isLoading || hasError}
          >
            {categories.map((category: string) => (
              <Select.OptGroup key={category} label={category}>
                {categoryDataset
                  .filter((item) => item.label === category)
                  .map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {item.value}
                    </Select.Option>
                  ))}
              </Select.OptGroup>
            ))}
          </Select>
        </Label>
      </div>

      <div className={styles.controlItem}>
        <Label label='Y-axis Variable'>
          <Select
            id='Analytics-ScatterPlot-VariableY_Input'
            value={variableY ? variableY.Index : undefined}
            style={{ width: '100%' }}
            onChange={(active) => onVariableChange(active, 'Y')}
            disabled={isLoading || hasError}
          >
            {variablesY
              .filter((item) => item.Name !== 'Time')
              .map((item) => (
                <Select.Option key={item.Index} value={item.Index}>
                  {item.Name}
                </Select.Option>
              ))}
          </Select>
        </Label>
      </div>

      <div className={styles.controlItem}>
        <Label label='X-axis Intervals'>
          <InputNumber
            id='Analytics-ScatterPlot-XAxisIntervals_Input'
            value={xAxisInterval}
            min={0.1}
            style={{ width: '100%' }}
            onChange={(active) => onXAxisIntervalChange(active)}
            disabled={isLoading || hasError}
          />
        </Label>
      </div>

      <div className={styles.controlItem}>
        <Label label='Y-axis Intervals'>
          <InputNumber
            id='Analytics-ScatterPlot-YAxisIntervals_Input'
            value={yAxisInterval}
            step={5}
            min={0.1}
            style={{ width: '100%' }}
            onChange={(active) => onYAxisIntervalChange(active)}
            disabled={isLoading || hasError}
          />
        </Label>
      </div>

      <div className={styles.controlItem}>
        <Label label='Month'>
          <Select
            id='Analytics-ScatterPlot-Month_Input'
            value={month}
            style={{ width: '100%' }}
            onChange={(active) => onMonthChange(active)}
            disabled={isLoading || hasError}
          >
            {monthOptions.map((item) => (
              <Select.Option key={item.value} value={item.value}>
                {item.label}
              </Select.Option>
            ))}
          </Select>
        </Label>
      </div>

      {dataPreformanceWarning && (
        <div className={styles.performanceWarning}>
          <div className={styles.warningText}>
            Over 100,000 data points (10 years of 1-hourly data) may affect
            performance.
          </div>
        </div>
      )}
    </div>
  );
};

export type { ScatterPlotAdditionalInputProps };
